// 암호화 키 생성 (32바이트)
const generateKey = async (): Promise<CryptoKey> => {
    return await window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256
      },
      true,
      ['encrypt', 'decrypt']
    );
  };
  
  // 문자열을 ArrayBuffer로 변환
  const stringToArrayBuffer = (str: string): Uint8Array => {
    const encoder = new TextEncoder();
    return encoder.encode(str);
  };
  
  // ArrayBuffer를 문자열로 변환
  const arrayBufferToString = (buffer: ArrayBuffer): string => {
    const decoder = new TextDecoder();
    return decoder.decode(buffer);
  };
  
  // ArrayBuffer를 Base64로 인코딩
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };
  
  // Base64를 ArrayBuffer로 디코딩
  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  };
  
  // 암호화 함수
  export const encrypt = async (data: string): Promise<string> => {
    try {
      const key = await generateKey();
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const encodedData = stringToArrayBuffer(data);
      
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        encodedData
      );
      
      // 키를 내보내기 (Base64로 인코딩)
      const exportedKey = await window.crypto.subtle.exportKey('raw', key);
      
      // 암호화된 데이터와 IV, 키를 하나의 객체로 결합
      const encryptedData = {
        data: arrayBufferToBase64(encryptedBuffer),
        iv: arrayBufferToBase64(iv),
        key: arrayBufferToBase64(exportedKey)
      };
      
      return JSON.stringify(encryptedData);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  };
  
  // 복호화 함수
  export const decrypt = async (encryptedString: string): Promise<string> => {
    try {
      const encryptedData = JSON.parse(encryptedString);
      
      // Base64 디코딩
      const encryptedBuffer = base64ToArrayBuffer(encryptedData.data);
      const iv = base64ToArrayBuffer(encryptedData.iv);
      const keyBuffer = base64ToArrayBuffer(encryptedData.key);
      
      // 키 가져오기
      const key = await window.crypto.subtle.importKey(
        'raw',
        keyBuffer,
        {
          name: 'AES-GCM',
          length: 256
        },
        false,
        ['decrypt']
      );
      
      // 복호화
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: new Uint8Array(iv)
        },
        key,
        encryptedBuffer
      );
      
      return arrayBufferToString(decryptedBuffer);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data');
    }
  };
  
  // 안전한 로컬 스토리지 저장 함수
  export const setEncryptedItem = async (key: string, value: string): Promise<void> => {
    try {
      const encryptedValue = await encrypt(value);
      window.localStorage.setItem(key, encryptedValue);
    } catch (error) {
      console.error('Failed to set encrypted item:', error);
      // 암호화 실패 시 원본 데이터 저장 (fallback)
      window.localStorage.setItem(key, value);
    }
  };
  
  // 안전한 로컬 스토리지 읽기 함수
  export const getEncryptedItem = async (key: string): Promise<string | null> => {
    try {
      const encryptedValue = window.localStorage.getItem(key);
      if (!encryptedValue) return null;
      
      // JSON 형식인지 확인 (암호화된 데이터)
      if (encryptedValue.startsWith('{')) {
        return await decrypt(encryptedValue);
      } else {
        // 기존 암호화되지 않은 데이터 (fallback)
        return encryptedValue;
      }
    } catch (error) {
      console.error('Failed to get encrypted item:', error);
      // 복호화 실패 시 원본 데이터 반환 (fallback)
      return window.localStorage.getItem(key);
    }
  }; 